<template>
	<div class="modal-container ModalAddEditFinanceChargeFee">
		<div class="modal-header">
			<div>
				Edit Finance Charge Fee Details
				<div class="additional-info">
					Origin: {{ feeSourceEnumsHelper.getDisplay(fee.source) }}
					<span v-if="$global.isAdminView && fee">(Title ID: {{ fee.titleId ?? 'N/A' }}, Original: {{ fee.originalTitleId ?? 'N/A' }})</span>
				</div>
			</div>
		</div>
		<div class="modal-body">
			<PanelAddEditFeeInfo
				:fee
				:isDisabled="modal.isModalDisabled"
				:bo
				:isDealLocked="modal.isDealLocked"
			/>
		</div>
		<div class="modal-footer modal-footer-split">
			<div>
				<button
					class="button-unwind"
					@click="deleteFinanceChargeFee"
					:disabled="modal.isModalDisabled"
				>
					Delete Fee
				</button>
			</div>
			<div v-if="modal.isModalDisabled">
				<button
					class="button-unwind"
					@click="cancel()"
				>
					Cancel
				</button>
				<button
					class="button-save"
					:disabled="modal.isModalDisabled || v$.$invalid"
					@click="save()"
				>
					Save
				</button>
			</div>
			<div v-else>
				<button
					class="button-error"
					@click="cancel()"
				>
					Close
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	export interface IModalAddEditFinanceChargeFeeProps {
		fimenu: FIMenu;
		fee: FIMenuBuyersOrderFee;
		bo: FIMenuBuyersOrder;
		isModalDisabled: boolean;
		isDealLocked: boolean;
	}
</script>

<script setup lang="ts">
	// #region Imports
	import { computed, ref } from 'vue';
	import $modal from '@core/services/modal';
	import FeeSourceEnumsHelper from '@core/_enums/FeeSourceEnumsHelper';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuBuyersOrder from '@core/classes/FIMenuBuyersOrder';
	import FIMenuBuyersOrderFee from '@core/classes/FIMenuBuyersOrderFee';
	import PanelAddEditFeeInfo from '@/components/panel/PanelAddEditFeeInfo.vue';
	import { required } from '@vuelidate/validators';
	import { useVuelidate } from '@vuelidate/core';
	// #endregion

	// #region Props
	const { modal } = defineProps<{
		modal: IModalAddEditFinanceChargeFeeProps;
	}>();
	// #endregion

	// #region States

	const fee = ref<FIMenuBuyersOrderFee>(new FIMenuBuyersOrderFee(modal.fee));
	const bo = ref<FIMenuBuyersOrder>(modal.bo);
	const fimenu = ref<FIMenu>(modal.fimenu);
	const feeSourceEnumsHelper = new FeeSourceEnumsHelper();

	const validations = computed(() => ({
		fee: {
			...(fee.value.validation() as any),
			name: {
				required,
				hasUniqueName,
			} as any,
		},
	}));
	const v$ = useVuelidate(validations, { fee });
	// #endregion

	// #region Methods

	// Remove the finance charge fee from the buyers order
	function deleteFinanceChargeFee() {
		const result = confirm('Are you sure you want to delete this fee?');
		if (result) {
			bo.value.removeFinanceChargeFee(fee.value);
			fimenu.value.recalculateBuyersOrder();
			$modal.cancel();
		}
	}

	// We moved this logic outside of FIMenuBuyersOrderFee because of base Fee's validation method
	function hasUniqueName() {
		return !bo.value.fees.find(
			(boFee: FIMenuBuyersOrderFee) => fee.value.id !== boFee.id && boFee.name && fee.value.name && boFee.name.toLowerCase() === fee.value.name.toLowerCase(),
		);
	}

	function save() {
		$modal.close(fee.value);
	}

	function cancel() {
		$modal.cancel();
	}
	// #endregion
</script>

<style>
	.ModalAddEditFinanceChargeFee .modal-header .additional-info {
		font-size: 0.75rem;
		color: var(--third-color);
	}
</style>
